import type { KartState } from '../physics/vehicle';

/** Closer than this and it is contact, not draft — collision handles it instead. */
const DRAFT_MIN_DIST = 2; // m
/** Beyond this the wake has dissipated. A real kart tow reaches out well past
 *  a single kart-length, which is what makes it worth chasing on a straight. */
const DRAFT_MAX_DIST = 22; // m
/** Must be tucked in behind, not alongside — narrower than the lane itself,
 *  but wide enough to stay in the tow through a small offset. */
const DRAFT_HALF_WIDTH = 1.8; // m
/** Aerodynamic drag cut by up to this fraction at the closest range. */
const DRAFT_MAX_REDUCTION = 0.35;

/**
 * Per-kart aerodynamic drag multiplier from drafting the kart(s) ahead.
 *
 * A kart sitting in another's wake — close behind and lined up with it, not
 * beside it — loses up to `DRAFT_MAX_REDUCTION` of its aero drag, fading out
 * over `DRAFT_MAX_DIST`. Only the strongest draft counts per kart; sitting
 * behind two karts at once does not stack.
 */
export function computeSlipstream(karts: Pick<KartState, 'x' | 'y' | 'heading'>[]): number[] {
  const n = karts.length;
  const scales = new Array(n).fill(1);

  for (let i = 0; i < n; i++) {
    const a = karts[i];
    const cosH = Math.cos(a.heading);
    const sinH = Math.sin(a.heading);
    let strongest = 0;

    for (let j = 0; j < n; j++) {
      if (i === j) continue;
      const b = karts[j];
      const dx = b.x - a.x;
      const dy = b.y - a.y;
      const forward = dx * cosH + dy * sinH;
      if (forward < DRAFT_MIN_DIST || forward > DRAFT_MAX_DIST) continue;
      const lateral = -dx * sinH + dy * cosH;
      if (Math.abs(lateral) > DRAFT_HALF_WIDTH) continue;

      const strength = 1 - (forward - DRAFT_MIN_DIST) / (DRAFT_MAX_DIST - DRAFT_MIN_DIST);
      if (strength > strongest) strongest = strength;
    }

    scales[i] = 1 - DRAFT_MAX_REDUCTION * strongest;
  }

  return scales;
}
